import { createFileRoute } from "@tanstack/react-router";
import { docToOptionDefinitions } from "doc/options/docGetInstanceOptionSets";
import useShowroomPlanData from "lib/components/showroom2/data/useShowroomPlanData";
import ShowroomIBSDemo from "lib/components/showroom2/pages/ShowroomIBSDemo";
import { defaultShowroomDataState } from "lib/components/showroom2/state/showroomState";
import useShowroomModal from "lib/components/showroom2/state/useShowroomModal";
import useShowroomState, {
  ShowroomProvider,
  useSetShowroomState,
} from "lib/components/showroom2/state/useShowroomState";
import { randomOptionChoices } from "lib/components/studio/inspector/randomOptionChoices";
import { useCallback, useEffect, useState } from "react";
import Link from "ui/components/Link";
import ProductName from "ui/components/ProductName";
import type { OptionProps } from "ui/forms/Select";
import useBroadcastChannelSync from "ui/hooks/useBroadcastChannelSync";
import IconArrow from "ui/icons/IconArrow";
import IconHigharc from "ui/icons/IconHigharcLockupHorizontal";
import styles from "./index.module.scss";

function ShowroomSandboxPage() {
  const planData = useShowroomPlanData();
  const studioDocId = planData?.masterPlan?.studioDocId;
  const builderId = planData?.builder?.id;
  const communityPlanId = planData?.communityPlan?.id;
  // Used to remount & restart the UX
  const [mountKey, setMountKey] = useState(0);

  return (
    <ShowroomProvider key={mountKey}>
      <ShowroomIBSDemo
        header={
          <DemoBanner
            studioDocId={studioDocId}
            builderId={builderId}
            communityPlanId={communityPlanId}
            remount={() => setMountKey((prev) => prev + 1)}
            mountKey={mountKey}
          />
        }
      />
    </ShowroomProvider>
  );
}

// Adds header UI for controlling the IBS demo
function DemoBanner({
  studioDocId,
  builderId,
  communityPlanId,
  remount,
  mountKey,
}: {
  studioDocId?: string;
  builderId?: string;
  communityPlanId?: string;
  remount: () => void;
  mountKey: number;
}) {
  const { openModal, updateModal, closeModal } = useShowroomModal();
  const [modal] = useShowroomState("modal");
  const setOptionChoices = useSetShowroomState("optionChoices");

  // Sync with Sheets
  const [pages, setPages] = useState<OptionProps[]>([]);
  const [pageFilter, setPageFilter] = useState(0);

  useEffect(() => {
    /** Open a new tab with Procure and Live Plans in iframes in a split screen
     *  NOTE: Showroom is running a community plan, while Procure and Live Plans are running the underlying studio doc, as that's all they currently support.
     *  This could result in discrepancies due to option overrides
     *  */
    if (
      studioDocId &&
      builderId &&
      communityPlanId &&
      !pages && // skip if already received broadcast (prevents hot-reloading re-opening new windows)
      mountKey === 0 // only run on first mount
    ) {
      const params = new URLSearchParams(window.location.search);
      const fuzzer = params.get("fuzzer") === "0" ? "&fuzzer=0" : "";
      window.open(
        `${window.location.origin}/demo/connected/bigscreen?builderId=${builderId}&id=${studioDocId}&communityPlanId=${communityPlanId}${fuzzer}`,
        "_blank",
      );
    }
  }, [builderId, studioDocId, communityPlanId, pages, mountKey]);

  // Get pages from Sheets
  useBroadcastChannelSync({ setState: setPages, channelId: studioDocId, channelName: "demo/sheets_pages" });
  // Get and set current sheet
  useBroadcastChannelSync({
    state: pageFilter,
    setState: setPageFilter,
    channelId: studioDocId,
    channelName: "demo/sheets_pageFilter",
  });

  // Stream prop updates to the modal - a bit of a hack, as I'm gluing components together that aren't really meant for this
  useEffect(() => {
    updateModal({
      children: <SheetsModal pages={pages} pageFilter={pageFilter} setPageFilter={setPageFilter} />,
      modalKey: "sheets",
    });
  }, [pageFilter]);

  // Start over: reset state
  const restart = useCallback(() => {
    // Reset option chocies
    setOptionChoices(defaultShowroomDataState.optionChoices);
    // Reset Sheet
    setPageFilter(0);
    // Remount: resets viewer and scrolls to top
    // Trigger after state updates to avoid race condition
    requestAnimationFrame(remount);
  }, []);

  // Open start modal on mount - trigger idle timer onClose
  useEffect(() => {
    let timeout: NodeJS.Timeout;

    function setTimer() {
      clearTimeout(timeout);
      // Open countdown modal
      timeout = setTimeout(
        () =>
          openModal({
            children: <RestartingModal restart={restart} closeModal={closeModal} />,
            className: styles.emptyModal,
            noDefaultStyle: true,
            modalKey: "restarting",
          }),
        1000 * 60 * 3, // restart after 3 minutes idle time
      );
    }

    openModal({
      children: <StartModal closeModal={closeModal} studioDocId={studioDocId} />,
      className: styles.emptyModal,
      noDefaultStyle: true,
      modalKey: "start",
      // Start idle timer: restarts on each event
      onClose: () => {
        setTimer();
        document.addEventListener("click", setTimer);
        document.addEventListener("touchstart", setTimer);
        document.addEventListener("keydown", setTimer);
        document.addEventListener("wheel", setTimer);
      },
    });

    return () => {
      clearTimeout(timeout);
      document.removeEventListener("click", setTimer);
      document.removeEventListener("touchstart", setTimer);
      document.removeEventListener("keydown", setTimer);
      document.removeEventListener("wheel", setTimer);
    };
  }, []);

  // Broadcast start modal open state
  useBroadcastChannelSync({
    state: modal.modalKey === "start" && modal.open,
    channelId: studioDocId,
    channelName: "demo/ibs-2024/showroom_startModal",
  });

  // Update StartModal to init fuzzing once sheets have rendered
  useEffect(() => {
    if (pages) {
      updateModal({
        children: <StartModal closeModal={closeModal} studioDocId={studioDocId} syncReady />,
        modalKey: "start",
      });
    }
  }, [pages]);

  return (
    <div className={styles.banner}>
      <ProductName productName="Sandbox" className={styles.logo} />
      <div className={styles.buttons}>
        <Link selectedCx={styles.selected} selected className={styles.showroom}>
          Showroom
        </Link>

        <Link
          selectedCx={styles.selected}
          onClick={() =>
            openModal({
              title: (
                <div className={styles.modalTitle}>
                  <ProductName productName="Live Sheets" />
                  <span>- Automatic construction-ready plans</span>
                </div>
              ),
              children: <SheetsModal pages={pages} pageFilter={pageFilter} setPageFilter={setPageFilter} />,
              size: "medium",
              className: styles.demoModal,
              modalKey: "sheets",
            })
          }
        >
          Live Sheets
        </Link>

        <Link className={styles.start} onClick={restart}>
          Start Over
        </Link>
      </div>
    </div>
  );
}

function StartModal({
  closeModal,
  syncReady,
  studioDocId,
}: {
  closeModal: () => void;
  syncReady?: boolean;
  studioDocId: string | undefined;
}) {
  const [doc] = useShowroomState("doc");
  const setOptionChoices = useSetShowroomState("optionChoices");
  const [initialBuildingRendered] = useShowroomState("initialBuildingRendered");
  const [pricing] = useShowroomState("pricing");

  // Broadcast summary / pricing for fuzzed home
  useBroadcastChannelSync({
    state: pricing,
    channelId: studioDocId,
    channelName: "demo/ibs-2024/showroom_summary",
  });

  // Option Fuzzer
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    const params = new URLSearchParams(window.location.search);
    // start fuzzing
    if (
      doc &&
      params.get("fuzzer") !== "0" && // param to disable the fuzzer in case it causes issues
      initialBuildingRendered && // wait until rendered
      syncReady // ready when other tab is rendered, prevents backup
    ) {
      const exploreOptionChoices = () => {
        timeout = setTimeout(() => {
          setOptionChoices((prev) => ({ ...prev, ...randomOptionChoices(docToOptionDefinitions(doc)) }));

          exploreOptionChoices();
        }, 5000); // change every 5 seconds
      };
      exploreOptionChoices();
    }
    return () => {
      setOptionChoices(defaultShowroomDataState.optionChoices);
      clearTimeout(timeout);
    };
  }, [doc, initialBuildingRendered, syncReady]);

  return (
    <div className={styles.startModal} onClick={closeModal}>
      <IconHigharc />
      <div className={styles.title}>The Homebuilding Cloud</div>
      <Link>
        Design a home
        <IconArrow />
      </Link>
      <div className={styles.description}>
        Generate <span>automatic CDs</span> and <span>instant estimates</span> for every <span>personalized</span> home.
      </div>
    </div>
  );
}

function SheetsModal({
  pages,
  pageFilter,
  setPageFilter,
}: {
  pages: OptionProps[];
  pageFilter: number;
  setPageFilter: (p: number) => void;
}) {
  return (
    <div className={styles.sheetsModal}>
      {pages?.map((page) => (
        <Link
          className={styles.sheet}
          selectedCx={styles.selected}
          selected={pageFilter === Number(page.value)}
          key={page.value?.toString()}
          onClick={() => setPageFilter(Number(page.value))}
        >
          {page.label}
        </Link>
      ))}
    </div>
  );
}

//  Countdown to restart - click anywhere to close and cancel
function RestartingModal({ restart, closeModal }: { restart: () => void; closeModal: () => void }) {
  const [seconds, setSeconds] = useState(5);

  useEffect(() => {
    if (seconds < 2) {
      restart();
    }
    const timeout = setTimeout(() => {
      setSeconds((prev) => prev - 1);
    }, 1000);

    return () => clearTimeout(timeout);
  }, [seconds]);

  return (
    <div className={styles.restartingModal} onClick={closeModal}>
      <div className={styles.description}>
        Restarting in {seconds} second{seconds !== 1 ? "s" : ""}...
      </div>
      <Link>Cancel</Link>
    </div>
  );
}

export const Route = createFileRoute("/demo/connected/showroom")({
  component: ShowroomSandboxPage,
  staticData: { isPublicPage: true },
});
